import { useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  EnvironmentKind,
  OnboardProjectInput,
  PolicyAction,
  PolicyMode,
  ProjectKind,
  ProjectTemplateDTO,
} from "@app-factory/shared";
import {
  CONNECTION_TEMPLATES,
  DEFAULT_PROJECT_POLICIES,
  PolicyAction as PolicyActionEnum,
} from "@app-factory/shared";
import { api, ApiRequestError } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { useAuth } from "@/auth/auth";
import {
  Btn,
  Field,
  Panel,
  inputClass,
  toneColor,
  type Tone,
} from "@/design/primitives";
import { kindLabel } from "@/design/project-visual";

/**
 * Onboarding wizard (spec §16, /Design „ONBOARDING WIZARD“):
 * levý sloupec kroků (volitelné mimo základ).
 * Soubory (podklady + design) se drží v paměti a nahrají po vytvoření projektu.
 */

type StepKey =
  | "basics"
  | "template"
  | "applications"
  | "departments"
  | "uploads"
  | "design"
  | "codebases"
  | "environments"
  | "connections"
  | "policies"
  | "goals"
  | "milestone"
  | "summary";

interface StepDef {
  key: StepKey;
  label: string;
  optional: boolean;
}

const STEPS: StepDef[] = [
  { key: "basics", label: cs.wizard.steps.basics, optional: false },
  { key: "template", label: cs.wizard.steps.template, optional: true },
  { key: "applications", label: cs.wizard.steps.applications, optional: true },
  { key: "departments", label: cs.wizard.steps.departments, optional: true },
  { key: "uploads", label: cs.wizard.steps.uploads, optional: true },
  { key: "design", label: cs.wizard.steps.design, optional: true },
  { key: "codebases", label: cs.wizard.steps.codebases, optional: true },
  { key: "environments", label: cs.wizard.steps.environments, optional: true },
  { key: "connections", label: cs.wizard.steps.connections, optional: true },
  { key: "policies", label: cs.wizard.steps.policies, optional: true },
  { key: "goals", label: cs.wizard.steps.goals, optional: true },
  { key: "milestone", label: cs.wizard.steps.milestone, optional: true },
  { key: "summary", label: cs.wizard.steps.summary, optional: false },
];

interface DraftApp {
  name: string;
  description: string;
  designFile?: File;
}
interface DraftCodebase {
  name: string;
  repoUrl: string;
  description: string;
  appNames: string[];
}
interface DraftEnv {
  name: string;
  kind: EnvironmentKind;
  deployPolicy: PolicyMode;
}
interface DraftGoal {
  title: string;
  metric: string;
  /** YYYY-MM-DD z date inputu, "" = bez deadline. */
  deadline: string;
}
interface DraftMilestone {
  title: string;
  note: string;
  deadline: string;
}
interface DraftConnection {
  key: string;
  name: string;
}

function isoFromDate(date: string): string | null {
  return date ? new Date(`${date}T00:00:00`).toISOString() : null;
}

const MODE_TONE: Record<PolicyMode, Tone> = {
  AUTO: "green",
  APPROVAL: "amber",
  DENIED: "red",
};
const NEXT_MODE: Record<PolicyMode, PolicyMode> = {
  AUTO: "APPROVAL",
  APPROVAL: "DENIED",
  DENIED: "AUTO",
};

export function OnboardingWizardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const templates = useQuery({
    queryKey: ["templates"],
    queryFn: api.templates,
  });

  const [stepIndex, setStepIndex] = useState(0);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState<ProjectKind>("SAAS");
  const [templateKey, setTemplateKey] = useState<string | null>(null);
  const [apps, setApps] = useState<DraftApp[]>([]);
  const [sourceFiles, setSourceFiles] = useState<File[]>([]);
  const [codebases, setCodebases] = useState<DraftCodebase[]>([]);
  const [envs, setEnvs] = useState<DraftEnv[] | null>(null); // null = ze šablony
  const [policyOverrides, setPolicyOverrides] = useState<
    Partial<Record<PolicyAction, PolicyMode>>
  >({});
  const [departments, setDepartments] = useState<string[]>([]);
  const [goals, setGoals] = useState<DraftGoal[]>([]);
  const [milestone, setMilestone] = useState<DraftMilestone>({
    title: "",
    note: "",
    deadline: "",
  });
  const [connections, setConnections] = useState<DraftConnection[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<"idle" | "creating" | "uploading">("idle");

  const template = useMemo(
    () => templates.data?.templates.find((t) => t.key === templateKey) ?? null,
    [templates.data, templateKey],
  );

  if (session?.user.role !== "OWNER" && session?.user.role !== "MANAGER") {
    return (
      <Panel className="mx-auto mt-16 max-w-md p-6 text-center">
        <div className="af-label" style={{ color: "var(--critical)" }}>
          PŘÍSTUP ODEPŘEN
        </div>
        <p
          className="mt-2 text-[11px]"
          style={{ color: "var(--text-secondary)" }}
        >
          Projekty zakládá owner nebo manager.
        </p>
      </Panel>
    );
  }

  const step = STEPS[stepIndex]!;

  /**
   * Stav kroku v levém sloupci (p5c): vyplněný krok má ✓, přeskočený to
   * přizná. Bez toho vypadalo všech dvanáct volitelných kroků stejně —
   * z odbytého sloupce nešlo poznat, co ještě čeká.
   */
  const filledSteps: Record<string, boolean> = {
    basics: name.trim().length > 0,
    template: templateKey !== null,
    applications: apps.length > 0,
    departments: departments.length > 0,
    uploads: sourceFiles.length > 0,
    design: apps.some((a) => a.designFile != null),
    codebases: codebases.length > 0,
    environments: envs !== null && envs.length > 0,
    connections: connections.length > 0,
    policies: Object.keys(policyOverrides).length > 0,
    goals: goals.length > 0,
    milestone: milestone.title.trim().length > 0,
    summary: false,
  };
  const effectivePolicies: Record<PolicyAction, PolicyMode> = {
    ...DEFAULT_PROJECT_POLICIES,
    ...(template?.config.policies ?? {}),
    ...policyOverrides,
  };

  const goRel = (delta: number) => {
    setStepIndex(Math.max(0, Math.min(STEPS.length - 1, stepIndex + delta)));
  };

  const create = async () => {
    setPhase("creating");
    setError(null);
    try {
      const payload: OnboardProjectInput = {
        name: name.trim(),
        description: description.trim(),
        kind,
        lifecycle: "DISCOVERY",
        businessPriority: "NORMAL",
        ...(templateKey ? { templateKey } : {}),
        applications: apps.map((a) => ({
          name: a.name,
          description: a.description,
        })),
        codebases: codebases.map((c) => ({
          name: c.name,
          description: c.description,
          repoUrl: c.repoUrl.trim() || null,
          defaultBranch: "main",
          techStack: [],
          applicationSlugs: c.appNames
            .map((n) => apps.find((a) => a.name === n))
            .filter((a): a is DraftApp => !!a)
            .map((a) => slugifyClient(a.name)),
        })),
        environments: (envs ?? []).map((e) => ({
          name: e.name,
          kind: e.kind,
          deployPolicy: e.deployPolicy,
          url: null,
        })),
        policies: policyOverrides,
        departments,
        goals: goals.map((g) => ({
          title: g.title,
          description: "",
          metric: g.metric,
          unit: "",
          deadline: isoFromDate(g.deadline),
        })),
        milestone:
          milestone.title.trim().length >= 2
            ? {
                title: milestone.title.trim(),
                note: milestone.note.trim(),
                deadline: isoFromDate(milestone.deadline),
              }
            : null,
        connections: connections.map((c) => ({
          key: c.key,
          name: c.name,
          note: "",
        })),
      } as OnboardProjectInput;

      const { project } = await api.onboardProject(payload);

      // po vzniku projektu nahrajeme držené soubory
      setPhase("uploading");
      for (const file of sourceFiles) {
        await api.uploadFile(project.id, file);
      }
      const designApps = apps.filter((a) => a.designFile);
      if (designApps.length) {
        const created = await api.applications(project.id);
        for (const a of designApps) {
          const match = created.applications.find((x) => x.name === a.name);
          if (match && a.designFile)
            await api.uploadDesign(match.id, a.designFile);
        }
      }

      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      void navigate({ to: "/projects/$slug", params: { slug: project.slug } });
    } catch (err) {
      setPhase("idle");
      if (err instanceof ApiRequestError) {
        // validace vrací details: [{path, message}] — bez nich je „Neplatný vstup" k ničemu
        const details = Array.isArray(err.details)
          ? (err.details as { path?: string; message?: string }[])
              .map((d) => [d.path, d.message].filter(Boolean).join(": "))
              .filter(Boolean)
          : [];
        setError(
          details.length
            ? `${err.message} — ${details.join(" · ")}`
            : err.message,
        );
      } else {
        setError(cs.common.error);
      }
    }
  };

  return (
    <div
      className="af-animate-fade grid min-h-[560px] grid-cols-1 gap-0 lg:grid-cols-[300px_1fr]"
      style={{
        borderRadius: "var(--radius-box)",
        border: "1px solid var(--border-default)",
      }}
    >
      {/* sidebar kroků — na mobilu horizontálně scrollovatelný pás nad obsahem */}
      <div className="p-4 max-lg:[border-bottom:1px_solid_var(--border-subtle)] lg:p-5 lg:[border-right:1px_solid_var(--border-subtle)]">
        <div
          className="af-display text-[17px] font-light"
          style={{ color: "var(--text-primary)" }}
        >
          {cs.wizard.title}
        </div>
        <div
          className="mb-4 mt-1 text-[11px]"
          style={{ color: "var(--text-tertiary)" }}
        >
          {cs.wizard.subtitle}
        </div>
        <div className="flex gap-1 overflow-x-auto whitespace-nowrap lg:flex-col lg:gap-0.5 lg:overflow-x-visible lg:whitespace-normal">
          {STEPS.map((s, i) => {
            const active = i === stepIndex;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setStepIndex(i)}
                className="flex flex-none cursor-pointer items-center gap-2.5 px-2.5 py-1.5 text-left"
                style={{
                  background: active
                    ? "color-mix(in srgb, var(--accent) 6%, transparent)"
                    : "transparent",
                  borderLeft: `2px solid ${active ? "var(--accent)" : "transparent"}`,
                }}
                data-testid={`wizard-step-${s.key}`}
              >
                <span
                  className="af-mono w-5 text-[11px]"
                  style={{
                    color: active ? "var(--accent)" : "var(--text-tertiary)",
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span
                  className="flex-1 text-[12px]"
                  style={{
                    color: active
                      ? "var(--text-primary)"
                      : "var(--text-secondary)",
                  }}
                >
                  {s.label}
                </span>
                <span
                  className="text-[11px]"
                  style={{
                    color: filledSteps[s.key]
                      ? "var(--positive)"
                      : "var(--text-tertiary)",
                  }}
                >
                  {filledSteps[s.key]
                    ? "✓"
                    : s.optional
                      ? i < stepIndex
                        ? cs.wizard.skipped
                        : cs.wizard.optional
                      : ""}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* obsah kroku */}
      <div className="flex flex-col p-4 sm:p-7">
        <div className="af-label" style={{ color: "var(--accent)" }}>
          {cs.wizard.step(stepIndex + 1, STEPS.length)}
        </div>
        <div
          className="af-display mb-5 mt-1 text-[21px] font-normal"
          style={{ color: "var(--text-primary)" }}
        >
          {step.label}
        </div>

        <div className="max-w-2xl flex-1">
          {step.key === "basics" ? (
            <div className="flex max-w-xl flex-col gap-4">
              <Field label={cs.fields.projectName}>
                <input
                  className={inputClass}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                  data-testid="wizard-name"
                />
              </Field>
              <Field
                label={cs.fields.description}
                hint="celý produkt/business kontext — ne jen repozitář"
              >
                <textarea
                  className={`${inputClass} min-h-[80px] resize-y`}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  data-testid="wizard-description"
                />
              </Field>
            </div>
          ) : null}

          {step.key === "template" ? (
            <>
              {/* druh projektu se nevybírá dvakrát — nese ho šablona */}
              <div
                className="mb-3 text-[12px]"
                style={{ color: "var(--text-tertiary)" }}
              >
                {cs.wizard.templateNote}{" "}
                <span style={{ color: "var(--accent)" }}>
                  {kindLabel[kind] || "JINÝ"}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {(templates.data?.templates ?? []).map((t) => (
                  <TemplateCard
                    key={t.key}
                    template={t}
                    selected={templateKey === t.key}
                    onSelect={() => {
                      setTemplateKey(templateKey === t.key ? null : t.key);
                      if (t.config.kind && templateKey !== t.key)
                        setKind(t.config.kind);
                    }}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setTemplateKey(null);
                    setKind("OTHER");
                  }}
                  className="cursor-pointer p-4 text-left"
                  style={{
                    borderRadius: "var(--radius-card)",
                    border: `1px solid ${templateKey === null && kind === "OTHER" ? "var(--accent-border)" : "var(--border-default)"}`,
                    background:
                      templateKey === null && kind === "OTHER"
                        ? "var(--accent-subtle)"
                        : "var(--surface)",
                  }}
                  data-testid="wizard-template-none"
                >
                  <div className="af-card-title">{cs.wizard.noTemplate}</div>
                  <div
                    className="mt-1.5 text-[12px]"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    {cs.wizard.noTemplateNote}
                  </div>
                </button>
              </div>
            </>
          ) : null}

          {step.key === "applications" ? (
            <AppsStep apps={apps} setApps={setApps} />
          ) : null}

          {step.key === "departments" ? (
            <DepartmentsStep
              selected={departments}
              setSelected={setDepartments}
            />
          ) : null}

          {step.key === "uploads" ? (
            <FilesStep
              files={sourceFiles}
              onAdd={(f) => setSourceFiles((prev) => [...prev, ...f])}
              onRemove={(i) =>
                setSourceFiles((prev) => prev.filter((_, x) => x !== i))
              }
            />
          ) : null}

          {step.key === "design" ? (
            <DesignStep apps={apps} setApps={setApps} />
          ) : null}

          {step.key === "codebases" ? (
            <CodebasesStep
              codebases={codebases}
              setCodebases={setCodebases}
              apps={apps}
            />
          ) : null}

          {step.key === "environments" ? (
            <EnvsStep envs={envs} setEnvs={setEnvs} template={template} />
          ) : null}

          {step.key === "connections" ? (
            <ConnectionsStep
              connections={connections}
              setConnections={setConnections}
            />
          ) : null}

          {step.key === "goals" ? (
            <GoalsStep goals={goals} setGoals={setGoals} />
          ) : null}

          {step.key === "milestone" ? (
            <MilestoneStep milestone={milestone} setMilestone={setMilestone} />
          ) : null}

          {step.key === "policies" ? (
            <div className="max-w-xl">
              <div
                className="mb-2 text-[11px]"
                style={{ color: "var(--text-tertiary)" }}
              >
                {cs.projectSettings.policiesNote} · kliknutím přepínáš AUTO →
                APPROVAL → DENIED
              </div>
              {PolicyActionEnum.options.map((action) => (
                <div
                  key={action}
                  className="flex items-center justify-between gap-2 py-2 text-[12px]"
                  style={{ borderTop: "1px solid var(--border-subtle)" }}
                >
                  <span
                    className="min-w-0 break-words"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {POLICY_LABELS[action]}
                  </span>
                  <button
                    type="button"
                    className="flex-none cursor-pointer px-2 py-0.5 text-[11px] tracking-[0.1em]"
                    style={{
                      borderRadius: "var(--radius-box)",
                      color: toneColor[MODE_TONE[effectivePolicies[action]]],
                      border: `1px solid ${toneColor[MODE_TONE[effectivePolicies[action]]]}`,
                    }}
                    onClick={() =>
                      setPolicyOverrides((prev) => ({
                        ...prev,
                        [action]: NEXT_MODE[effectivePolicies[action]],
                      }))
                    }
                  >
                    {effectivePolicies[action]}
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          {step.key === "summary" ? (
            <SummaryStep
              name={name}
              kind={kind}
              template={template}
              apps={apps}
              codebases={codebases}
              envs={envs}
              sourceFiles={sourceFiles}
              departments={departments}
              goals={goals}
              milestone={milestone}
              connections={connections}
            />
          ) : null}
        </div>

        {error ? (
          <div
            className="mt-3 text-[12px]"
            style={{ color: "var(--critical)" }}
          >
            ⚠ {error}
          </div>
        ) : null}

        {/* footer akce dle /Design */}
        <div
          className="mt-6 flex flex-wrap gap-2.5 pt-4"
          style={{ borderTop: "1px solid var(--border-subtle)" }}
        >
          {stepIndex > 0 ? (
            <Btn onClick={() => goRel(-1)}>{cs.wizard.back}</Btn>
          ) : null}
          {step.key === "summary" ? (
            <Btn
              variant="primary"
              disabled={name.trim().length < 2 || phase !== "idle"}
              onClick={() => void create()}
              data-testid="wizard-create"
            >
              {phase === "idle" ? cs.wizard.create : cs.wizard.creating}
            </Btn>
          ) : (
            <>
              <Btn
                variant="primary"
                disabled={step.key === "basics" && name.trim().length < 2}
                onClick={() => goRel(1)}
                data-testid="wizard-next"
              >
                {cs.wizard.next}
              </Btn>
              {step.optional ? (
                <Btn onClick={() => goRel(1)}>{cs.wizard.skip}</Btn>
              ) : null}
            </>
          )}
          <Btn
            variant="danger"
            className="ml-auto"
            onClick={() => void navigate({ to: "/projects" })}
          >
            {cs.wizard.cancel}
          </Btn>
        </div>
      </div>
    </div>
  );
}

const POLICY_LABELS: Record<PolicyAction, string> = {
  TASK_FROM_SCOPE: "AI vytváří tasky ze schváleného scope",
  PROPOSAL_AUTO_APPROVE: "Nová feature bez tvého schválení",
  KNOWLEDGE_AUTO_UPDATE: "AI samo aktualizuje Knowledge",
  BRANCH_CREATE: "Vytvořit branch",
  PR_CREATE: "Otevřít pull request",
  PR_MERGE: "Merge PR",
  SUPPORT_SEND: "Odeslat odpověď zákazníkovi",
};

function slugifyClient(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

// ---------------------------------------------------------------------------

function TemplateCard({
  template,
  selected,
  onSelect,
}: {
  template: ProjectTemplateDTO;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="cursor-pointer p-4 text-left"
      style={{
        borderRadius: "var(--radius-box)",
        border: `1px solid ${selected ? "var(--accent)" : "var(--border-default)"}`,
        background: selected
          ? "color-mix(in srgb, var(--accent) 5%, transparent)"
          : "var(--surface)",
        boxShadow: selected
          ? "0 0 14px color-mix(in srgb, var(--accent) 15%, transparent)"
          : undefined,
      }}
      data-testid={`template-${template.key}`}
    >
      <div
        className="af-display text-[13px] font-semibold"
        style={{ color: "var(--text-primary)" }}
      >
        {template.name}
      </div>
      <div
        className="mt-1.5 text-[11px] leading-relaxed"
        style={{ color: "var(--text-secondary)" }}
      >
        {template.description}
      </div>
      <div
        className="mt-2.5 text-[11px] leading-relaxed"
        style={{ color: "var(--text-tertiary)" }}
      >
        {template.config.environments.length} environments
        <br />
        Knowledge: {template.config.knowledgeFolders.join(" / ")}
      </div>
    </button>
  );
}

function AppsStep({
  apps,
  setApps,
}: {
  apps: DraftApp[];
  setApps: (a: DraftApp[]) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  return (
    <div className="max-w-xl">
      <div
        className="mb-3 text-[11px]"
        style={{ color: "var(--text-tertiary)" }}
      >
        Aplikace = uživatelské celky (Admin, zákaznický web, mobilní app…).
        Codebases přiřadíš v dalším kroku.
      </div>
      {apps.map((a, i) => (
        <div
          key={i}
          className="mb-2 flex items-center justify-between gap-2 px-3 py-2"
          style={{
            borderRadius: "var(--radius-box)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          <span
            className="min-w-0 break-words text-[11px]"
            style={{ color: "var(--text-primary)" }}
          >
            {a.name}
            <span style={{ color: "var(--text-tertiary)" }}>
              {" "}
              {a.description ? `· ${a.description}` : ""}
            </span>
          </span>
          <button
            type="button"
            className="flex-none cursor-pointer text-[11px]"
            style={{ color: "var(--critical)" }}
            onClick={() => setApps(apps.filter((_, x) => x !== i))}
          >
            ✕
          </button>
        </div>
      ))}
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
        <Field label={cs.fields.appName}>
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="wizard-app-name"
          />
        </Field>
        <Field label={cs.fields.description}>
          <input
            className={inputClass}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <Btn
          disabled={name.trim().length < 2}
          onClick={() => {
            setApps([
              ...apps,
              { name: name.trim(), description: description.trim() },
            ]);
            setName("");
            setDescription("");
          }}
          data-testid="wizard-app-add"
        >
          +
        </Btn>
      </div>
    </div>
  );
}

function DepartmentsStep({
  selected,
  setSelected,
}: {
  selected: string[];
  setSelected: (keys: string[]) => void;
}) {
  const departments = useQuery({
    queryKey: ["departments"],
    queryFn: api.departments,
  });
  const rows = (departments.data?.departments ?? []).filter((d) => d.enabled);
  return (
    <div className="max-w-xl">
      <div
        className="mb-3 text-[11px]"
        style={{ color: "var(--text-tertiary)" }}
      >
        {cs.wizard.departmentsHint}
      </div>
      {rows.map((d) => {
        const on = selected.includes(d.key);
        const color =
          toneColor[
            (d.color as Tone) in toneColor ? (d.color as Tone) : "cyan"
          ];
        return (
          <button
            key={d.key}
            type="button"
            onClick={() =>
              setSelected(
                on ? selected.filter((k) => k !== d.key) : [...selected, d.key],
              )
            }
            className="mb-1.5 flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2 text-left text-[12px]"
            style={{
              borderRadius: "var(--radius-box)",
              border: `1px solid ${on ? color : "var(--border-subtle)"}`,
              background: on
                ? "color-mix(in srgb, var(--accent) 4%, transparent)"
                : "transparent",
            }}
            data-testid={`wizard-department-${d.key}`}
          >
            <span
              className="min-w-0 break-words"
              style={{
                color: on ? "var(--text-primary)" : "var(--text-secondary)",
              }}
            >
              {d.name}
              <span style={{ color: "var(--text-tertiary)" }}>
                {d.description ? ` · ${d.description}` : ""}
              </span>
            </span>
            <span
              className="flex-none text-[11px]"
              style={{ color: on ? color : "var(--text-tertiary)" }}
            >
              {on ? "✓" : "+"}
            </span>
          </button>
        );
      })}
      {!rows.length && !departments.isLoading ? (
        <div className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
          {cs.common.none}
        </div>
      ) : null}
    </div>
  );
}

function GoalsStep({
  goals,
  setGoals,
}: {
  goals: DraftGoal[];
  setGoals: (g: DraftGoal[]) => void;
}) {
  const [title, setTitle] = useState("");
  const [metric, setMetric] = useState("");
  const [deadline, setDeadline] = useState("");
  return (
    <div className="max-w-xl">
      <div
        className="mb-3 text-[11px]"
        style={{ color: "var(--text-tertiary)" }}
      >
        {cs.wizard.goalsHint}
      </div>
      {goals.map((g, i) => (
        <div
          key={i}
          className="mb-2 flex items-center justify-between gap-2 px-3 py-2 text-[12px]"
          style={{
            borderRadius: "var(--radius-box)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          <span
            className="min-w-0 break-words"
            style={{ color: "var(--text-primary)" }}
          >
            ◎ {g.title}
            <span style={{ color: "var(--text-tertiary)" }}>
              {g.metric ? ` · ${g.metric}` : ""}
              {g.deadline ? ` · do ${g.deadline}` : ""}
            </span>
          </span>
          <button
            type="button"
            className="flex-none cursor-pointer text-[11px]"
            style={{ color: "var(--critical)" }}
            onClick={() => setGoals(goals.filter((_, x) => x !== i))}
          >
            ✕
          </button>
        </div>
      ))}
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
        <Field label={cs.fields.target}>
          <input
            className={inputClass}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="wizard-goal-title"
          />
        </Field>
        <Field label={cs.fields.metricOptional}>
          <input
            className={inputClass}
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
          />
        </Field>
        <Field label={cs.fields.deadline}>
          <input
            type="date"
            className={inputClass}
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
          />
        </Field>
        <Btn
          disabled={title.trim().length < 3}
          onClick={() => {
            setGoals([
              ...goals,
              { title: title.trim(), metric: metric.trim(), deadline },
            ]);
            setTitle("");
            setMetric("");
            setDeadline("");
          }}
          data-testid="wizard-goal-add"
        >
          +
        </Btn>
      </div>
    </div>
  );
}

function MilestoneStep({
  milestone,
  setMilestone,
}: {
  milestone: DraftMilestone;
  setMilestone: (m: DraftMilestone) => void;
}) {
  return (
    <div className="flex max-w-xl flex-col gap-4">
      <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        {cs.wizard.milestoneHint}
      </div>
      <Field label={cs.fields.milestoneName}>
        <input
          className={inputClass}
          value={milestone.title}
          onChange={(e) =>
            setMilestone({ ...milestone, title: e.target.value })
          }
          data-testid="wizard-milestone-title"
        />
      </Field>
      <Field label={cs.fields.note}>
        <input
          className={inputClass}
          value={milestone.note}
          onChange={(e) => setMilestone({ ...milestone, note: e.target.value })}
        />
      </Field>
      <Field label={cs.fields.deadline}>
        <input
          type="date"
          className={inputClass}
          value={milestone.deadline}
          onChange={(e) =>
            setMilestone({ ...milestone, deadline: e.target.value })
          }
        />
      </Field>
    </div>
  );
}

function ConnectionsStep({
  connections,
  setConnections,
}: {
  connections: DraftConnection[];
  setConnections: (c: DraftConnection[]) => void;
}) {
  const [customName, setCustomName] = useState("");
  const customKey = slugifyClient(customName);
  const toggle = (key: string, name: string) => {
    setConnections(
      connections.some((c) => c.key === key)
        ? connections.filter((c) => c.key !== key)
        : [...connections, { key, name }],
    );
  };
  return (
    <div className="max-w-xl">
      <div
        className="mb-3 text-[11px]"
        style={{ color: "var(--text-tertiary)" }}
      >
        {cs.wizard.connectionsHint}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {CONNECTION_TEMPLATES.map((t) => {
          const on = connections.some((c) => c.key === t.key);
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => toggle(t.key, t.name)}
              className="cursor-pointer px-2.5 py-1.5 text-[11px]"
              style={{
                borderRadius: "var(--radius-box)",
                border: `1px solid ${on ? "color-mix(in srgb, var(--accent) 50%, transparent)" : "var(--border-default)"}`,
                color: on ? "var(--accent)" : "var(--text-secondary)",
                background: on
                  ? "color-mix(in srgb, var(--accent) 8%, transparent)"
                  : "transparent",
              }}
              data-testid={`wizard-connection-${t.key}`}
            >
              {t.icon} {t.name}
            </button>
          );
        })}
      </div>
      {connections
        .filter((c) => !CONNECTION_TEMPLATES.some((t) => t.key === c.key))
        .map((c) => (
          <div
            key={c.key}
            className="mt-2 flex items-center justify-between gap-2 px-3 py-2 text-[12px]"
            style={{
              borderRadius: "var(--radius-box)",
              border: "1px solid var(--border-subtle)",
            }}
          >
            <span
              className="min-w-0 break-words"
              style={{ color: "var(--text-primary)" }}
            >
              ↗ {c.name}{" "}
              <span style={{ color: "var(--text-tertiary)" }}>({c.key})</span>
            </span>
            <button
              type="button"
              className="flex-none cursor-pointer text-[11px]"
              style={{ color: "var(--critical)" }}
              onClick={() =>
                setConnections(connections.filter((x) => x.key !== c.key))
              }
            >
              ✕
            </button>
          </div>
        ))}
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
        <Field label={cs.wizard.connectionsCustom}>
          <input
            className={inputClass}
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            data-testid="wizard-connection-custom"
          />
        </Field>
        <Btn
          disabled={
            customName.trim().length < 2 ||
            customKey.length < 2 ||
            connections.some((c) => c.key === customKey)
          }
          onClick={() => {
            setConnections([
              ...connections,
              { key: customKey, name: customName.trim() },
            ]);
            setCustomName("");
          }}
        >
          +
        </Btn>
      </div>
    </div>
  );
}

function FilesStep({
  files,
  onAdd,
  onRemove,
}: {
  files: File[];
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  return (
    <div className="max-w-xl">
      <div
        className="mb-3 text-[11px]"
        style={{ color: "var(--text-tertiary)" }}
      >
        Raw podklady projektu (MD, PDF, ZIP, poznámky). Uloží se do Uploads po
        vytvoření projektu.
      </div>
      {files.map((f, i) => (
        <div
          key={i}
          className="mb-2 flex items-center justify-between gap-2 px-3 py-2 text-[12px]"
          style={{
            borderRadius: "var(--radius-box)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          <span
            className="min-w-0 break-words"
            style={{ color: "var(--text-primary)" }}
          >
            📄 {f.name}
          </span>
          <button
            type="button"
            className="flex-none cursor-pointer text-[11px]"
            style={{ color: "var(--critical)" }}
            onClick={() => onRemove(i)}
          >
            ✕
          </button>
        </div>
      ))}
      <div
        className="cursor-pointer px-4 py-6 text-center text-[11px]"
        style={{
          border: `1px dashed ${dragOver ? "var(--accent)" : "color-mix(in srgb, var(--accent) 20%, transparent)"}`,
          color: "var(--text-tertiary)",
        }}
        onClick={() => ref.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          onAdd(Array.from(e.dataTransfer.files));
        }}
      >
        {cs.knowledge.dropzone} — nebo klikni
      </div>
      <input
        ref={ref}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          onAdd(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />
    </div>
  );
}

function DesignStep({
  apps,
  setApps,
}: {
  apps: DraftApp[];
  setApps: (a: DraftApp[]) => void;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  if (apps.length === 0) {
    return (
      <div className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
        Nejdřív přidej aplikace (krok 3) — design se váže ke konkrétní aplikaci.
        Interní utilita design nepotřebuje.
      </div>
    );
  }
  return (
    <div className="max-w-xl">
      {apps.map((a, i) => (
        <div
          key={i}
          className="mb-2 flex flex-wrap items-center justify-between gap-2 px-3 py-2.5"
          style={{
            borderRadius: "var(--radius-box)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          <span
            className="min-w-0 break-words text-[11px]"
            style={{ color: "var(--text-primary)" }}
          >
            {a.name}
          </span>
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            {a.designFile ? (
              <span
                className="min-w-0 break-words text-[11px]"
                style={{ color: "var(--positive)" }}
              >
                ✓ {a.designFile.name}
              </span>
            ) : (
              <span
                className="text-[11px]"
                style={{ color: "var(--text-tertiary)" }}
              >
                bez designu
              </span>
            )}
            <button
              type="button"
              className="cursor-pointer px-2 py-1 text-[11px]"
              style={{
                border:
                  "1px dashed color-mix(in srgb, var(--accent) 25%, transparent)",
                color: "var(--accent)",
              }}
              onClick={() => refs.current[i]?.click()}
            >
              {cs.apps.uploadZip}
            </button>
            <input
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="file"
              accept=".zip,application/zip"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file)
                  setApps(
                    apps.map((x, xi) =>
                      xi === i ? { ...x, designFile: file } : x,
                    ),
                  );
                e.target.value = "";
              }}
            />
          </span>
        </div>
      ))}
    </div>
  );
}

function CodebasesStep({
  codebases,
  setCodebases,
  apps,
}: {
  codebases: DraftCodebase[];
  setCodebases: (c: DraftCodebase[]) => void;
  apps: DraftApp[];
}) {
  const [name, setName] = useState("");
  const [repoUrl, setRepoUrl] = useState("");
  const [appNames, setAppNames] = useState<string[]>([]);
  return (
    <div className="max-w-xl">
      <div
        className="mb-3 text-[11px]"
        style={{ color: "var(--text-tertiary)" }}
      >
        Projekt může mít víc repozitářů. Vazby na aplikace vytvoří základ
        Codebase Map.
      </div>
      {codebases.map((c, i) => (
        <div
          key={i}
          className="mb-2 flex items-center justify-between gap-2 px-3 py-2 text-[12px]"
          style={{
            borderRadius: "var(--radius-box)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          <span
            className="min-w-0 break-words"
            style={{ color: "var(--text-primary)" }}
          >
            ⌥ {c.name}
            <span style={{ color: "var(--text-tertiary)" }}>
              {c.repoUrl ? ` · ${c.repoUrl}` : ""}{" "}
              {c.appNames.length ? `· apps: ${c.appNames.join(", ")}` : ""}
            </span>
          </span>
          <button
            type="button"
            className="flex-none cursor-pointer text-[11px]"
            style={{ color: "var(--critical)" }}
            onClick={() => setCodebases(codebases.filter((_, x) => x !== i))}
          >
            ✕
          </button>
        </div>
      ))}
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field label={cs.fields.codebaseName}>
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="wizard-codebase-name"
          />
        </Field>
        <Field label={cs.fields.repoUrlOptional}>
          <input
            className={inputClass}
            value={repoUrl}
            onChange={(e) => setRepoUrl(e.target.value)}
          />
        </Field>
      </div>
      {apps.length ? (
        <div className="mt-2">
          <div className="af-label-dim mb-1.5">APLIKACE V TOMTO REPU</div>
          <div className="flex flex-wrap gap-1.5">
            {apps.map((a) => (
              <button
                key={a.name}
                type="button"
                onClick={() =>
                  setAppNames((prev) =>
                    prev.includes(a.name)
                      ? prev.filter((x) => x !== a.name)
                      : [...prev, a.name],
                  )
                }
                className="cursor-pointer px-2 py-1 text-[11px]"
                style={{
                  borderRadius: "var(--radius-box)",
                  border: `1px solid ${appNames.includes(a.name) ? "color-mix(in srgb, var(--accent) 50%, transparent)" : "var(--border-default)"}`,
                  color: appNames.includes(a.name)
                    ? "var(--accent)"
                    : "var(--text-secondary)",
                }}
              >
                {a.name}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <div className="mt-3">
        <Btn
          disabled={name.trim().length < 2}
          onClick={() => {
            setCodebases([
              ...codebases,
              {
                name: name.trim(),
                repoUrl: repoUrl.trim(),
                description: "",
                appNames,
              },
            ]);
            setName("");
            setRepoUrl("");
            setAppNames([]);
          }}
          data-testid="wizard-codebase-add"
        >
          + PŘIDAT
        </Btn>
      </div>
    </div>
  );
}

function EnvsStep({
  envs,
  setEnvs,
  template,
}: {
  envs: DraftEnv[] | null;
  setEnvs: (e: DraftEnv[]) => void;
  template: ProjectTemplateDTO | null;
}) {
  const effective: DraftEnv[] =
    envs ??
    (template?.config.environments ?? []).map((e) => ({
      name: e.name,
      kind: e.kind,
      deployPolicy: e.deployPolicy,
    }));
  const [name, setName] = useState("");
  const [kind, setKind] = useState<EnvironmentKind>("STAGING");

  return (
    <div className="max-w-xl">
      <div
        className="mb-3 text-[11px]"
        style={{ color: "var(--text-tertiary)" }}
      >
        Deploy policy per environment — kliknutím přepneš AUTO → APPROVAL →
        DENIED.
        {envs === null && template ? " Předvyplněno ze šablony." : ""}
      </div>
      {effective.map((e, i) => (
        <div
          key={i}
          className="mb-1.5 flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-[12px]"
          style={{
            borderRadius: "var(--radius-box)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          <span
            className="min-w-0 break-words"
            style={{ color: "var(--text-primary)" }}
          >
            {e.name}{" "}
            <span style={{ color: "var(--text-tertiary)" }}>({e.kind})</span>
          </span>
          <span className="flex flex-none items-center gap-2">
            <button
              type="button"
              className="cursor-pointer px-2 py-0.5 text-[11px] tracking-[0.1em]"
              style={{
                borderRadius: "var(--radius-box)",
                color: toneColor[MODE_TONE[e.deployPolicy]],
                border: `1px solid ${toneColor[MODE_TONE[e.deployPolicy]]}`,
              }}
              onClick={() =>
                setEnvs(
                  effective.map((x, xi) =>
                    xi === i
                      ? { ...x, deployPolicy: NEXT_MODE[x.deployPolicy] }
                      : x,
                  ),
                )
              }
            >
              deploy {e.deployPolicy}
            </button>
            <button
              type="button"
              className="cursor-pointer text-[11px]"
              style={{ color: "var(--critical)" }}
              onClick={() => setEnvs(effective.filter((_, x) => x !== i))}
            >
              ✕
            </button>
          </span>
        </div>
      ))}
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
        <Field label={cs.fields.name}>
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label={cs.fields.type}>
          <select
            className={inputClass}
            style={{ background: "var(--surface-inset)" }}
            value={kind}
            onChange={(e) => setKind(e.target.value as EnvironmentKind)}
          >
            {(
              [
                "LOCAL",
                "DEVELOPMENT",
                "STAGING",
                "PRODUCTION",
                "CUSTOM",
              ] as const
            ).map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </Field>
        <Btn
          disabled={name.trim().length < 2}
          onClick={() => {
            setEnvs([
              ...effective,
              { name: name.trim(), kind, deployPolicy: "APPROVAL" },
            ]);
            setName("");
          }}
        >
          +
        </Btn>
      </div>
    </div>
  );
}

function SummaryStep({
  name,
  kind,
  template,
  apps,
  codebases,
  envs,
  sourceFiles,
  departments,
  goals,
  milestone,
  connections,
}: {
  name: string;
  kind: ProjectKind;
  template: ProjectTemplateDTO | null;
  apps: DraftApp[];
  codebases: DraftCodebase[];
  envs: DraftEnv[] | null;
  sourceFiles: File[];
  departments: string[];
  goals: DraftGoal[];
  milestone: DraftMilestone;
  connections: DraftConnection[];
}) {
  const effectiveEnvs = envs ?? template?.config.environments ?? [];
  const rows: [string, string][] = [
    ["PROJEKT", `${name || "—"} (${kind})`],
    ["ŠABLONA", template ? template.name : "bez šablony"],
    ["APLIKACE", apps.length ? apps.map((a) => a.name).join(", ") : "—"],
    [
      "CODEBASES",
      codebases.length ? codebases.map((c) => c.name).join(", ") : "—",
    ],
    ["ODDĚLENÍ", departments.length ? departments.join(", ") : "—"],
    [
      "ENVIRONMENTS",
      effectiveEnvs.length
        ? effectiveEnvs.map((e) => `${e.name} (${e.deployPolicy})`).join(", ")
        : "—",
    ],
    [
      "CONNECTIONS",
      connections.length ? connections.map((c) => c.name).join(", ") : "—",
    ],
    ["GOALS", goals.length ? goals.map((g) => g.title).join(", ") : "—"],
    [
      "MILESTONE",
      milestone.title.trim().length >= 2
        ? `${milestone.title.trim()}${milestone.deadline ? ` · do ${milestone.deadline}` : ""}`
        : cs.wizard.noMilestone,
    ],
    [
      "PODKLADY",
      sourceFiles.length ? `${sourceFiles.length} souborů → Uploads` : "—",
    ],
    [
      "DESIGN",
      apps.some((a) => a.designFile)
        ? apps
            .filter((a) => a.designFile)
            .map((a) => a.name)
            .join(", ")
        : "—",
    ],
  ];
  return (
    <div className="max-w-xl">
      <Panel brackets className="p-5">
        {rows.map(([k, v]) => (
          <div key={k} className="mb-2 flex gap-3 text-[12px]">
            <span className="w-24 flex-none af-label-dim sm:w-32">{k}</span>
            <span
              className="min-w-0 break-words"
              style={{ color: "var(--text-primary)" }}
            >
              {v}
            </span>
          </div>
        ))}
      </Panel>
      <div
        className="mt-3 text-[11px] leading-relaxed"
        style={{ color: "var(--text-tertiary)" }}
      >
        {cs.wizard.summaryNote}
      </div>
    </div>
  );
}
